/**
 * FaithStage — rites at the shrine: a glowing halo on the ground, a soft
 * column of light over the altar, a ring of candles, slow incense motes,
 * and (founding rites) banners. The congregation bows toward the altar.
 */
import * as THREE from 'three/webgpu';
import { glowTexture, ringTexture, spriteOf } from '../fx_textures.js';
import { type Stage, type StageCtx, disposeGroup, makeBanner, waveCloth, propMaterial } from './StageBase.js';
import { ceremonyProp } from '../ceremony_props_loader.js';

interface Mote { s: THREE.Sprite; ttl: number; life: number; ox: number; oz: number }

export class FaithStage implements Stage {
  readonly group = new THREE.Group();
  private candles: THREE.Sprite[] = [];
  private motes: Mote[] = [];
  private halo: THREE.Sprite | null = null;
  private column: THREE.Sprite | null = null;
  private banners: THREE.Group[] = [];
  private generated = 0;

  constructor(private readonly ctx: StageCtx) {
    const { x, z, y, def } = ctx;
    this.group.position.set(x, y, z);
    ctx.root.add(this.group);
    const props = new Set(def.props);
    // Altar at the host slot (+1.2, 0): the generated shrine altar (Hugging
    // Face wave), its front toward the congregation (−x); the stone block
    // stands in until it lands.
    const genAltar = ceremonyProp('altar', -Math.PI / 2);
    if (genAltar) { genAltar.position.set(2.2, 0, 0); this.group.add(genAltar); this.generated++; }
    else {
      const stone = propMaterial(0x9a948c, 0.9);
      const altar = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.9, 0.7), stone);
      altar.position.set(2.2, 0.45, 0);
      this.group.add(altar);
    }
    if (props.has('shrine_glow')) {
      this.halo = spriteOf(ringTexture(), 0xbfa8ff, 7, true, 0.55);
      this.halo.position.set(1.2, 0.12, 0);
      this.group.add(this.halo);
      this.column = spriteOf(glowTexture(), 0xd6c8ff, 2.2, true, 0.5);
      this.column.position.set(2.2, 1.7, 0);
      this.column.scale.set(1.6, 3.2, 1);
      this.group.add(this.column);
    }
    if (props.has('candles')) {
      const wax = propMaterial(0xf1e7c8, 0.9);
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        const cx = 1.2 + Math.cos(a) * 2.6, cz = Math.sin(a) * 2.6;
        const cy = ctx.groundAt(x + cx, z + cz) - y;
        const c = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.045, 0.3, 6), wax);
        c.position.set(cx, cy + 0.15, cz);
        this.group.add(c);
        const f = spriteOf(glowTexture(), 0xffcf7a, 0.32);
        f.position.set(cx, cy + 0.38, cz);
        this.group.add(f);
        this.candles.push(f);
      }
    }
    if (props.has('incense')) for (let i = 0; i < 6; i++) this.motes.push(this.mote(true));
    if (props.has('banners')) {
      for (const s of [-1, 1]) {
        const b = makeBanner(ctx.title, 0x4b3a8a);
        b.position.set(3.2, ctx.groundAt(x + 3.2, z + s * 2) - y, s * 2);
        b.rotation.y = Math.PI / 2;
        this.group.add(b);
        this.banners.push(b);
      }
    }
  }

  private mote(randomAge: boolean): Mote {
    const s = spriteOf(glowTexture(), 0xc9c2d6, 0.35, false, 0.25);
    const life = 5 + Math.random() * 3;
    const m: Mote = { s, ttl: randomAge ? Math.random() * life : life, life, ox: 2.2 + (Math.random() - 0.5) * 0.5, oz: (Math.random() - 0.5) * 0.5 };
    this.group.add(s);
    return m;
  }

  update(t: number, dt: number): void {
    this.candles.forEach((f, i) => {
      f.scale.setScalar(0.32 * (1 + Math.sin(t * (8 + i * 0.7) + i) * 0.2 + Math.sin(t * 12 + i * 2) * 0.08)); // flicker by scale
    });
    for (const m of this.motes) {
      m.ttl -= dt;
      if (m.ttl <= 0) { m.ttl = m.life; m.ox = 2.2 + (Math.random() - 0.5) * 0.5; m.oz = (Math.random() - 0.5) * 0.5; }
      const age = m.life - m.ttl;
      m.s.position.set(m.ox + Math.sin(t * 0.7 + age) * 0.3, 1.0 + age * 0.45, m.oz + Math.cos(t * 0.5 + age * 0.8) * 0.3);
      m.s.scale.setScalar((0.35 + age * 0.12) * Math.max(0.01, Math.sin((age / m.life) * Math.PI))); // fade by scale
    }
    if (this.halo) this.halo.scale.setScalar(7 + Math.sin(t * 0.9) * 0.3 + Math.sin(t * 1.3) * 0.2);
    if (this.column) this.column.scale.set(1.6 + Math.sin(t * 0.6) * 0.1, 3.2 + Math.sin(t * 0.6) * 0.2, 1);
    this.banners.forEach((b, i) => waveCloth(b, t, i));
  }

  describe(): Record<string, number | string | boolean> {
    return { family: 'faith', candles: this.candles.length, motes: this.motes.length, halo: !!this.halo, banners: this.banners.length, lights: 0, generated: this.generated };
  }

  dispose(): void { disposeGroup(this.group); }
}
